import React, { FC, useEffect, useState } from "react";
import apiService from "../../../services/apiService";
import { IStation } from "../../../models/IUser";
import { meterReplaceErrorMessage } from "../../../utils/meterCellError";
import "./PopupMeterReplace.css";

interface PopupMeterReplaceProps {
  station: IStation;
  onClose: () => void;
  onSaved: () => void;
}

interface FormState {
  value_at_uninstall: string;
  uninstalled_at: string;
  meter_number: string;
  meter_address: string;
  type: string;
  value_at_install: string;
  installed_at: string;
  remove_only: boolean;
}

const EMPTY: FormState = {
  value_at_uninstall: "",
  uninstalled_at: "",
  meter_number: "",
  meter_address: "",
  type: "",
  value_at_install: "",
  installed_at: "",
  remove_only: false,
};



/** `R.5`: текущее местное время в формате поля `datetime-local`. */
function nowLocal(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");

  return (
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
    `T${pad(now.getHours())}:${pad(now.getMinutes())}`
  );
}

/** `R.5`: времена снятия и установки заполнены при открытии, человек их правит. */
function initialForm(): FormState {
  const now = nowLocal();

  return { ...EMPTY, uninstalled_at: now, installed_at: now };
}

/**
 * `DEV-93`: форма замены счётчика у станции. Пять полей замера и прибора:
 * итоговое показание и время снятия, номер/адрес/тип, начальное показание и
 * время установки. «Снять без замены» закрывает установку и оставляет запись 0.
 */
export const PopupMeterReplace: FC<PopupMeterReplaceProps> = ({
  station,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = useState<FormState>(initialForm);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // `R.5`: Esc закрывает форму без отправки.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  // `DEV-93.2`: действующая запись с адресом 0 — «счётчика нет». Для неё форма
  // не спрашивает итоговое показание и время снятия: снимать нечего.
  const currentAddress = Number(
    station["Meters.meter_address"] ?? station.meter_address ?? 0,
  );
  const installing = !Number.isFinite(currentAddress) || currentAddress === 0;

  const change =
    (name: keyof FormState) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value;

      setForm((prev) => ({ ...prev, [name]: value }));
      setError(null);
    };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const payload: Record<string, unknown> = {
        remove_only: form.remove_only,
      };

      if (!form.remove_only) {
        // `DEV-93.2`: у записи 0 итогового показания и времени снятия нет —
        // запись закрывается временем установки на стороне сервера.
        if (!installing) {
          payload.value_at_uninstall = Number(form.value_at_uninstall);
          payload.uninstalled_at = new Date(form.uninstalled_at).toISOString();
        }

        payload.meter_number = form.meter_number;
        payload.meter_address = Number(form.meter_address);
        payload.type = form.type || undefined;
        payload.value_at_install = Number(form.value_at_install);
        payload.installed_at = form.installed_at
          ? new Date(form.installed_at).toISOString()
          : undefined;
      } else {
        payload.value_at_uninstall = Number(form.value_at_uninstall);
        payload.uninstalled_at = new Date(form.uninstalled_at).toISOString();
      }

      await apiService.POST(`/station/${station.id}/meter/replace`, payload);
      onSaved();
    } catch (err) {
      // Отказ back — в самой форме, а не общим тостом. `S.2.1`: заряд — по
      // коду `errors[0].code`, а не по тексту.
      setError(
        meterReplaceErrorMessage(
          err,
          station.controller_address ?? station.id,
        ),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="meter-replace-overlay"
      role="dialog"
      aria-modal="true"
      // `R.5`: клик по затемнению закрывает без отправки.
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <form className="meter-replace-form" onSubmit={submit}>
        <h3>
          {installing ? "Установка счётчика" : "Замена счётчика"} — станция{" "}
          {station.controller_address ?? station.id}
        </h3>

        {!installing ? (
          <>
            <label>
              Итоговое показание старого
              <input
                type="number"
                min="0"
                value={form.value_at_uninstall}
                onChange={change("value_at_uninstall")}
                required
              />
            </label>

            <label>
              Время снятия
              <input
                type="datetime-local"
                value={form.uninstalled_at}
                onChange={change("uninstalled_at")}
                required
              />
            </label>

            <label>
              <input
                type="checkbox"
                checked={form.remove_only}
                onChange={change("remove_only")}
              />
              Снять без замены
            </label>
          </>
        ) : null}

        {!form.remove_only ? (
          <>
            <label>
              Заводской номер нового
              <input
                type="text"
                value={form.meter_number}
                onChange={change("meter_number")}
                maxLength={20}
                required
              />
            </label>

            <label>
              Адрес нового (1…65534)
              <input
                type="number"
                min="1"
                max="65534"
                value={form.meter_address}
                onChange={change("meter_address")}
                required
              />
            </label>

            <label>
              Тип счётчика
              <input
                type="text"
                value={form.type}
                onChange={change("type")}
                maxLength={40}
              />
            </label>

            <label>
              Начальное показание нового
              <input
                type="number"
                min="0"
                value={form.value_at_install}
                onChange={change("value_at_install")}
                required
              />
            </label>

            <label>
              Время установки
              <input
                type="datetime-local"
                value={form.installed_at}
                onChange={change("installed_at")}
              />
            </label>
          </>
        ) : null}

        {error ? (
          <div className="Error_message" role="alert">
            {error}
          </div>
        ) : null}

        <div className="meter-replace-actions">
          <button type="submit" disabled={busy}>
            {busy ? "Сохраняем…" : installing ? "Установить" : "Заменить"}
          </button>
          <button type="button" onClick={onClose} disabled={busy}>
            Отмена
          </button>
        </div>
      </form>
    </div>
  );
};
